import type { CoviChatItem } from "../model/types"

/**
 * Contextual auto-title for a thread, derived from its first user message.
 *
 * The naive version of this was `firstUserMessage.slice(0, 60)`, which cuts
 * mid-word, keeps whatever markdown/code/URL noise the message opened with,
 * and renders a list of threads that all start "can you take a look at the...".
 * Here the message is reduced to the phrase a human would use to label it:
 * markup stripped, conversational preamble dropped, first clause only, cut on a
 * word boundary.
 *
 * Deliberately pure and deterministic — no network, no model call, so a title
 * exists the instant the first message lands. A server-generated title (a Sofia
 * `llm-generation` task) can later upgrade a thread by calling the same rename
 * path the user does; nothing else needs to change.
 */

export const DEFAULT_THREAD_TITLE = "New chat"

/** Long enough to stay meaningful, short enough not to wrap in the thread list. */
const MAX_TITLE_LENGTH = 56

/** Below this, a "first sentence" is a fragment ("Hi." / "Quick one.") — keep reading. */
const MIN_SENTENCE_LENGTH = 12

/**
 * Conversational preamble that carries no topic. Stripped repeatedly, so
 * "hey sofia, can you please ..." peels down to the actual request. Only filler
 * is listed — interrogatives ("what is", "why did") ARE the topic and stay.
 */
const LEADING_FILLER =
  /^(?:(?:hey|hi|hello|ok|okay)\s+sofia|(?:hey|hi|hello|ok|okay)|sofia|please|(?:can|could|would|will)\s+you|i(?:'d|\s+would)\s+like\s+you\s+to|i\s+want\s+you\s+to|i\s+need\s+you\s+to|help\s+me|let's|lets)\b[\s,:;.!-]*/i

/** Guards against a pathological message peeling forever. */
const MAX_FILLER_PASSES = 4

const SENTENCE_TERMINATORS = new Set([".", "!", "?", "\n"])

/** Reduce markdown/code/URL noise to the words a human would read aloud. */
function stripMarkup(text: string): string {
  return (
    text
      // Fenced code is never a title — drop it whole rather than titling a thread "```python".
      .replace(/```[\s\S]*?```/g, " ")
      .replace(/```[\s\S]*$/, " ")
      .replace(/`([^`]*)`/g, "$1")
      // Keep a link's text, drop its target.
      .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
      // A bare URL becomes its host — "app.coval.dev" labels a thread; the full path does not.
      .replace(/\bhttps?:\/\/([^\s/]+)\S*/gi, "$1")
      .replace(/^\s*#{1,6}\s+/gm, "")
      .replace(/^\s*[-*+]\s+/gm, "")
      .replace(/^\s*>\s?/gm, "")
      .replace(/[*~]{1,3}/g, "")
      // An underscore is emphasis ONLY at a word boundary. Coval's vocabulary is
      // snake_case (`run_2.4`, `post_call_intents`), and markdown itself does not
      // treat an intra-word `_` as emphasis — stripping those mangles every id.
      .replace(/(^|[^\p{L}\p{N}_])_{1,3}|_{1,3}([^\p{L}\p{N}_]|$)/gu, "$1$2")
      .replace(/\s+/g, " ")
      .trim()
  )
}

function stripLeadingFiller(text: string): string {
  let result = text
  for (let pass = 0; pass < MAX_FILLER_PASSES; pass += 1) {
    const next = result.replace(LEADING_FILLER, "")
    if (next === result) {
      break
    }
    result = next.trimStart()
  }
  return result
}

/**
 * First sentence, or the whole string when that sentence is a bare greeting
 * fragment. Scanned by hand rather than with a lookbehind split so a decimal
 * ("0.5s latency") or an id ("run_1.2") doesn't end the title early.
 */
function firstSentence(text: string): string {
  for (let index = 0; index < text.length; index += 1) {
    if (!SENTENCE_TERMINATORS.has(text[index])) {
      continue
    }
    const next = text[index + 1]
    // A terminator mid-token (1.5, e.g. run.id) is not a sentence break.
    if (next !== undefined && next !== " " && next !== "\n") {
      continue
    }
    const candidate = text.slice(0, index).trim()
    if (candidate.length >= MIN_SENTENCE_LENGTH) {
      return candidate
    }
  }
  return text.trim()
}

/** Cut to MAX_TITLE_LENGTH without splitting a word; ellipsis only when cut. */
function truncateOnWordBoundary(text: string): string {
  if (text.length <= MAX_TITLE_LENGTH) {
    return text
  }
  const clipped = text.slice(0, MAX_TITLE_LENGTH)
  const lastSpace = clipped.lastIndexOf(" ")
  // A single word longer than the cap has no boundary to fall back to.
  const body = lastSpace > 0 ? clipped.slice(0, lastSpace) : clipped
  return `${body.replace(/[\s,;:.!?-]+$/, "")}…`
}

/** Sentence-case only — never touch a word the user capitalized themselves. */
function capitalizeFirst(text: string): string {
  const first = text.charAt(0)
  return first.toLowerCase() === first && first.toUpperCase() !== first
    ? first.toUpperCase() + text.slice(1)
    : text
}

/** Title for one message's worth of text. */
function titleFromMessage(message: string): string {
  const stripped = stripMarkup(message)
  if (!stripped) {
    return DEFAULT_THREAD_TITLE
  }
  const meaningful = stripLeadingFiller(stripped)
  // Filler-only messages ("hey sofia") still deserve their words over "New chat".
  const body = firstSentence(meaningful || stripped)
  const title = truncateOnWordBoundary(body).trim()
  return title ? capitalizeFirst(title) : DEFAULT_THREAD_TITLE
}

/** Thread title derived from the first user message; DEFAULT until one lands. */
export function deriveThreadTitle(items: CoviChatItem[]): string {
  const firstUser = items.find((item) => item.kind === "user")
  const text = firstUser && "text" in firstUser ? firstUser.text : ""
  return text.trim() ? titleFromMessage(text) : DEFAULT_THREAD_TITLE
}
